// @flow

import React from "react";
import { localize } from "redux-i18n";
import { GSGrid } from "@components";

import { currencyTransform } from "@transformers/string";
import type {
  AbsenceRateReportChartType,
  AbsenceRateReport,
} from "@modules/reporting/reducer/trends/absence-rate/types";

type OwnProps = $Exact<{
  rows: Array<AbsenceRateReportChartType>,
  reportDetails?: AbsenceRateReport,
  getBreakdownReport: (row: Object, column: Object) => void,
  allowBreakdown: boolean,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export const TrendAbsenceRateReportTable = (props: Props) => {
  const trendType = ((props.reportDetails || {}).filters || {}).trendType;
  const showCurrency = trendType && trendType === "COST_OF_ABSENCE";

  return (
    <div className="w-100 absence-rate-trend-report-table">
      {props.rows[0] && (
        <GSGrid
          id="trend-absence-rate-report-table"
          rows={props.rows.map((row: Object) => {
            return {
              ...row,
              key: row.id,
              name: props.t(row.name),
              isExpanded: row.hierarchy.length <= 1,
            };
          })}
          groupColumn={{
            name: props.t("TRENDS_ABSENCE_RATE_GROUP_BY"),
            key: "name",
            frozen: "left",

            renderCell: ({ row }: { row: Object }) => {
              return row.name;
            },
          }}
          columns={[
            ...(props.rows[0]
              ? (props.rows[0].periods || []).map((period, idx) => {
                  return {
                    ...period,
                    name: period.key,
                    key: `${period.key}-${idx}`,

                    align: "center",
                    headerCellClass: "fs-12",
                    cellClass: "p-1px",
                    width: 100,
                    ...(props.allowBreakdown
                      ? {
                          cellTooltip: {
                            text: props.t("SEE_MORE"),
                            hide: ({ row }: { row: Object }) => !!row.hasChildren,
                          },
                        }
                      : {}),
                    renderCell: ({ row, column }: *) => {
                      const cellData = row.periods.find((r: Object) => {
                        return r.key === column.name;
                      });
                      if (!cellData) return null;
                      const { value, delta } = cellData;
                      return GSGrid.render.deltaArrowTrendIndicator(
                        showCurrency ? currencyTransform(value) : value,
                        delta,
                      );
                    },
                    ...(props.allowBreakdown
                      ? {
                          onCellClick: (item: *, column: *) => {
                            props.getBreakdownReport(item, column);
                          },
                        }
                      : {}),
                  };
                })
              : []),
            {
              key: "total",
              name: props.t("TOTAL"),
              width: 100,
              frozen: "right",
              cellClass: "p-1px",
              renderCell: ({ row }: *) => {
                const { total, max } = row;
                return GSGrid.render.barIndicator({
                  total: showCurrency ? currencyTransform(total) : total,
                  max: showCurrency ? currencyTransform(max) : max,
                  color: "rgba(255, 221, 180)",
                });
              },
            },
            {
              name: "Trend",
              key: "trend",
              width: 100,
              frozen: "right",
              cellClass: "p-1px",
              renderCell: ({ row }: { row: Object }) => {
                return GSGrid.render.sparklineIndicator({ rowHeight: 40, periods: row.periods });
              },
            },
          ]}
        />
      )}
    </div>
  );
};

export default localize()(TrendAbsenceRateReportTable);
